import type {
    ClientDetailResult,
    DetailInstallment,
    DetailItinerary,
} from '@/integrations/supabase/rpc-types';
import type { MoneyCopyKey } from '@/lib/i18n/money-copy';
import type { TranslationKey } from '@/types/i18n.types';
import { clientDisplayName } from '@/lib/dashboard/clients/client-display';
import { paymentMethodKey } from '@/lib/payments/utils/payment-method';

/**
 * Export XLSX della scheda cliente.
 *
 * Il file NON e' una seconda fonte di verita': ogni numero che ci finisce arriva gia'
 * calcolato da `get_client_detail`, che aggrega nel database sullo stesso perimetro RLS
 * del pannello. Qui dentro non si somma, non si conta e non si ricava nulla — si sceglie
 * la cella, l'etichetta e il formato. Se un giorno servisse un totale che il payload non
 * ha, il posto dove aggiungerlo e' la RPC, non questo file: un totale calcolato qui
 * divergerebbe in silenzio da quello mostrato a schermo.
 *
 * L'unica trasformazione ammessa e' l'appiattimento: le rate arrivano annidate dentro gli
 * itinerari perche' la tabella della UI le mostra cosi', mentre un foglio di calcolo le
 * vuole una per riga. Cambia la forma, non i valori.
 */

/** Firma di `t` da `useI18n`, presa come parametro per tenere il builder fuori da React. */
export type DossierT = (key: TranslationKey, options?: Record<string, unknown>) => string;

/*
  ---------------------------------------------------------------------------------------
  Controllo di forma del payload.

  Un export che scrive celle vuote invece di rompersi e' peggio di un export che fallisce:
  il file arriva, sembra buono, e chi lo riceve legge zero dove il numero non c'era. Questi
  elenchi sono i campi che il foglio legge davvero; se la RPC ne cambia uno, il bottone
  deve dirlo con il nome del campo mancante, non consegnare un dossier muto.

  "Mancante" significa chiave assente (`in`), non valore nullo: consulente, etichetta rata,
  data di pagamento e metodo sono legittimamente null su righe vere.
*/

const REQUIRED_SECTIONS = ['client', 'meta', 'stats', 'itineraries'] as const;

const REQUIRED_STATS_FIELDS = ['kpis', 'cashflow', 'destinations'] as const;

const REQUIRED_CLIENT_FIELDS = ['client_key', 'first_name', 'last_name', 'email', 'phone', 'has_account', 'first_seen_at', 'last_seen_at'] as const;

const REQUIRED_META_FIELDS = ['period', 'period_only', 'period_days', 'generated_on'] as const;

const REQUIRED_KPI_FIELDS = ['itineraries_count', 'requests_count', 'total_amount', 'paid_amount', 'remaining_amount', 'overdue_amount', 'closed_value_total', 'platform_fee_due_amount'] as const;

const REQUIRED_ITINERARY_FIELDS = ['itinerary_id', 'title', 'status', 'start_date', 'end_date', 'advisor_name', 'destinations', 'currency', 'has_plan', 'installments_count', 'plan_total', 'paid_amount', 'remaining_amount', 'overdue_amount', 'platform_fee_due_amount', 'created_at', 'completed_at', 'installments'] as const;

const REQUIRED_INSTALLMENT_FIELDS = ['seq', 'label', 'amount', 'due_date', 'status', 'paid_at', 'paid_amount', 'payment_method', 'is_overdue', 'platform_fee_due'] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const absent = (obj: Record<string, unknown>, keys: readonly string[]): string[] =>
    keys.filter((key) => !(key in obj));

export interface DossierPayloadReport {
    problems: string[];
    counts: { itinerari: number; rate: number; destinazioni: number; cashflow: number };
    /** Struttura sana ma nessuna riga: il file uscirebbe con le sole intestazioni. */
    emptyScope: boolean;
    /** Chiavi effettivamente ricevute, per il confronto quando qualcosa non torna. */
    receivedSections: string[];
}

/**
 * Confronta il payload ricevuto con quello che i fogli leggono.
 *
 * Non lancia: descrive. Chi chiama decide se fermarsi (struttura rotta) o proseguire
 * annotando il file (struttura sana ma vuota).
 */
export function inspectDossierPayload(data: unknown): DossierPayloadReport {
    const problems: string[] = [];
    const counts = { itinerari: 0, rate: 0, destinazioni: 0, cashflow: 0 };

    if (!isRecord(data)) {
        // Array e null sono i due sbagli plausibili — una RPC che torna una riga invece
        // dell'oggetto, o un cliente non visibile — e vanno nominati per quello che sono:
        // `typeof` li chiamerebbe entrambi "object" e la diagnosi resterebbe muta.
        const kind = data === null ? 'null' : Array.isArray(data) ? 'array' : typeof data;
        return {
            problems: [`Il payload non e' un oggetto (ricevuto: ${kind}).`],
            counts,
            emptyScope: true,
            receivedSections: [],
        };
    }

    const receivedSections = Object.keys(data);
    const missingSections = absent(data, REQUIRED_SECTIONS);
    if (missingSections.length > 0) {
        problems.push(
            `Sezioni mancanti: ${missingSections.join(', ')}. Ricevute: ${receivedSections.join(', ') || '(nessuna)'}.`
        );
        return { problems, counts, emptyScope: true, receivedSections };
    }

    // `stats.kpis` si controlla col suo percorso completo: nei messaggi deve comparire il
    // punto esatto del payload in cui guardare.
    const stats = isRecord(data.stats) ? data.stats : {};
    const objects: Array<[string, unknown, readonly string[]]> = [
        ['client', data.client, REQUIRED_CLIENT_FIELDS],
        ['meta', data.meta, REQUIRED_META_FIELDS],
        ['stats', data.stats, REQUIRED_STATS_FIELDS],
        ['stats.kpis', stats.kpis, REQUIRED_KPI_FIELDS],
    ];
    objects.forEach(([path, value, expected]) => {
        if (!isRecord(value)) {
            problems.push(`\`${path}\` non e' un oggetto (ricevuto: ${Array.isArray(value) ? 'array' : typeof value}).`);
            return;
        }
        const missing = absent(value, expected);
        if (missing.length > 0) {
            problems.push(
                `\`${path}\`: campi mancanti ${missing.join(', ')}. Presenti: ${Object.keys(value).join(', ')}.`
            );
        }
    });

    const arrays: Array<[string, unknown]> = [
        ['itineraries', data.itineraries],
        ['stats.cashflow', stats.cashflow],
        ['stats.destinations', stats.destinations],
    ];
    arrays.forEach(([path, value]) => {
        if (!Array.isArray(value)) {
            problems.push(`\`${path}\` non e' un array (ricevuto: ${typeof value}).`);
        }
    });

    const itineraries = Array.isArray(data.itineraries) ? data.itineraries : [];
    counts.itinerari = itineraries.length;
    counts.destinazioni = Array.isArray(stats.destinations) ? stats.destinations.length : 0;
    counts.cashflow = Array.isArray(stats.cashflow) ? stats.cashflow.length : 0;

    // Basta il primo itinerario: la RPC produce le righe con la stessa SELECT, se un campo
    // manca manca ovunque, e ispezionarle tutte allungherebbe l'elenco senza dire di piu'.
    const firstTrip = itineraries[0];
    if (isRecord(firstTrip)) {
        const missing = absent(firstTrip, REQUIRED_ITINERARY_FIELDS);
        if (missing.length > 0) {
            problems.push(
                `\`itineraries[0]\`: campi mancanti ${missing.join(', ')}. Presenti: ${Object.keys(firstTrip).join(', ')}.`
            );
        }
        itineraries.forEach((trip) => {
            if (isRecord(trip) && Array.isArray(trip.installments)) counts.rate += trip.installments.length;
        });
        const firstInstallment = Array.isArray(firstTrip.installments) ? firstTrip.installments[0] : undefined;
        if (isRecord(firstInstallment)) {
            const missingRate = absent(firstInstallment, REQUIRED_INSTALLMENT_FIELDS);
            if (missingRate.length > 0) {
                problems.push(
                    `\`itineraries[0].installments[0]\`: campi mancanti ${missingRate.join(', ')}. Presenti: ${Object.keys(firstInstallment).join(', ')}.`
                );
            }
        }
    }

    return {
        problems,
        counts,
        emptyScope: counts.itinerari === 0 && counts.destinazioni === 0 && counts.cashflow === 0,
        receivedSections,
    };
}

/** Errore con dentro l'elenco dei campi che non tornano: il messaggio e' la diagnosi. */
export class DossierPayloadError extends Error {
    readonly report: DossierPayloadReport;
    constructor(report: DossierPayloadReport) {
        super(`Payload di get_client_detail non conforme.\n- ${report.problems.join('\n- ')}`);
        this.name = 'DossierPayloadError';
        this.report = report;
    }
}

export interface BuildClientDossierOptions {
    t: DossierT;
    /** Prefisso del lessico del denaro scelto da `useMoneyCopy()` in base al ruolo. */
    money: MoneyCopyKey;
    /** Lingua per le date scritte come testo. Le date vere restano Date, non stringhe. */
    locale?: string;
}

/*
  Lo spazio prima dell'euro e' unificatore (U+00A0), scritto come escape e non come
  carattere: nel sorgente sarebbe indistinguibile da uno spazio normale, e chi passasse di
  qui a "sistemare" la spaziatura lo sostituirebbe senza accorgersene, lasciando che Excel
  mandi a capo il simbolo di valuta quando la colonna e' stretta.
*/
const MONEY_FORMAT = '#,##0.00\u00A0€';
const DATE_FORMAT = 'dd/mm/yyyy';
const DATETIME_FORMAT = 'dd/mm/yyyy hh:mm';

/**
 * `YYYY-MM-DD` -> Date locale a mezzogiorno.
 *
 * `new Date('2026-09-12')` e' mezzanotte UTC: a ovest di Greenwich Excel mostrerebbe l'11.
 * Mezzogiorno tiene la data ferma su qualunque fuso, e l'ora non viene mai mostrata perche'
 * la colonna e' formattata come data secca.
 */
const parseDateOnly = (iso: string | null | undefined): Date | null => {
    if (!iso) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0);
};

const parseTimestamp = (iso: string | null | undefined): Date | null => {
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** Etichetta di stato itinerario, con la stessa degradazione della tabella a schermo. */
const statusLabel = (status: string, t: DossierT): string => {
    const key = `itineraryStatus.${status}` as TranslationKey;
    const label = t(key);
    return label === key ? status : label;
};

const installmentStatusLabel = (installment: DetailInstallment, t: DossierT): string => {
    if (installment.status === 'cancelled') {
        return t('dashboard.overview.detail.installmentCancelled');
    }
    if (installment.status === 'paid') {
        return t('dashboard.overview.clients.export.installmentPaid');
    }
    return installment.is_overdue
        ? t('dashboard.overview.detail.installmentOverdue')
        : t('dashboard.overview.detail.installmentPending');
};

const methodLabel = (method: string | null, t: DossierT): string => {
    const key = paymentMethodKey(method);
    return key ? t(key) : '';
};

const yesNo = (value: boolean, t: DossierT): string =>
    t(value ? 'common.yes' : 'common.no');

/**
 * Come si legge il periodo in testa al Riepilogo.
 *
 * `period_only` e' l'interruttore del pannello: a `false` la scheda mostra tutta la storia
 * del cliente e il selettore di periodo della pagina non c'entra nulla. Scriverlo comunque
 * come "periodo: 30g" farebbe leggere il file come se fosse filtrato, ed e' l'equivoco che
 * questa riga esiste per evitare.
 */
const periodLabel = (meta: ClientDetailResult['meta'], t: DossierT): string => {
    // Le chiavi a schermo sono period7d / period30d / period90d / periodYtd.
    const named = t(
        `dashboard.overview.period${
            meta.period === 'ytd' ? 'Ytd' : meta.period
        }` as TranslationKey
    );

    return meta.period_only
        ? t('dashboard.overview.clients.export.periodOnlyValue', {
              period: named,
              days: meta.period_days,
          })
        : t('dashboard.overview.clients.export.periodFullValue', { period: named });
};

interface SheetColumn {
    header: string;
    width: number;
    /** Formato numerico Excel della colonna. Nessun calcolo: solo come si vede il numero. */
    numFmt?: string;
}

/** Intestazione in grassetto su fondo chiaro, ripetuta identica su tutti i fogli. */
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } } as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Ws = any;

const addTable = (sheet: Ws, columns: SheetColumn[], rows: unknown[][]): void => {
    sheet.columns = columns.map((column) => ({ width: column.width }));

    const header = sheet.addRow(columns.map((column) => column.header));
    header.font = { bold: true };
    header.eachCell((cell: Ws) => {
        cell.fill = HEADER_FILL;
    });

    rows.forEach((values) => {
        const row = sheet.addRow(values);
        columns.forEach((column, index) => {
            if (column.numFmt) row.getCell(index + 1).numFmt = column.numFmt;
        });
    });

    sheet.views = [{ state: 'frozen', ySplit: header.number }];
};

/** Riga di nota a tutta larghezza: spiega una colonna senza inventare una colonna in piu'. */
const addNote = (sheet: Ws, text: string): void => {
    const row = sheet.addRow([text]);
    row.font = { italic: true, color: { argb: 'FF6B7280' }, size: 9 };
};

/**
 * Costruisce il workbook e lo restituisce come Blob pronto al download.
 *
 * `exceljs` entra con un import dinamico: e' quasi un megabyte di bundle e serve solo a chi
 * preme il bottone, non a chi apre la dashboard.
 */
export async function buildClientDossierWorkbook(
    data: ClientDetailResult,
    { t, money, locale = 'it-IT' }: BuildClientDossierOptions
): Promise<Blob> {
    /*
      Prima di aprire il workbook: se la RPC ha cambiato forma ci si ferma qui, con i nomi
      dei campi che non tornano. Il caso che ha motivato questo controllo e' un file
      scaricato senza righe, indistinguibile — a valle — da un cliente senza viaggi.
    */
    const report = inspectDossierPayload(data);
    console.info('[dossier cliente] payload ricevuto', {
        sezioni: report.receivedSections,
        conteggi: report.counts,
        perimetroVuoto: report.emptyScope,
        periodo: { period: data?.meta?.period, period_only: data?.meta?.period_only },
        problemi: report.problems,
    });
    if (report.problems.length > 0) throw new DossierPayloadError(report);

    const module = await import('exceljs');
    // Il bundle browser di exceljs esporta il namespace come default, quello node no.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ExcelJS = ((module as any).default ?? module) as any;

    const workbook = new ExcelJS.Workbook();
    workbook.created = parseTimestamp(data.meta.generated_on) ?? new Date();

    /*
      Scheda attiva alla riapertura: la prima. Excel e LibreOffice ricordano nel file quale
      foglio era selezionato al salvataggio, e senza questo l'ordine delle linguette non
      basta a garantire su quale si apra. I viewer che mostrano un foglio solo mostrano
      questo.
    */
    workbook.views = [{ activeTab: 0, firstSheet: 0, visibility: 'visible' }];

    const client = data.client;
    const name = clientDisplayName(client);
    const kpis = data.stats.kpis;

    const labelPaid = t(`${money}.paid`);
    const labelPending = t(`${money}.pending`);
    const labelQuoted = t('dashboard.overview.clients.export.quotedTotal');

    /*
      I nomi dei fogli stanno in un posto solo perche' l'indice del Riepilogo li ripete: se
      si prendessero due volte da `t()` in punti diversi, rinominarne uno lascerebbe
      l'indice a puntare a una linguetta che non esiste piu'.
    */
    const sheetNames = {
        summary: t('dashboard.overview.clients.export.sheetSummary'),
        profile: t('dashboard.overview.clients.export.sheetProfile'),
        itineraries: t('dashboard.overview.clients.export.sheetItineraries'),
        payments: t('dashboard.overview.clients.export.sheetPayments'),
        destinations: t('dashboard.overview.clients.export.sheetDestinations'),
        fees: t('dashboard.overview.clients.export.sheetFees'),
    };

    // ------------------------------------------------------------ Riepilogo (primo foglio)
    /*
      Il Riepilogo apre il file, l'Anagrafica viene dopo.

      All'inizio era il contrario, ed e' costato un falso allarme: Anteprima di macOS mostra
      solo il primo foglio e non disegna le linguette, quindi chi apriva il dossier vedeva
      nome, email e telefono e concludeva che dentro non ci fosse nulla — "nessuna
      informazione su viaggi e cifre" descriveva esattamente l'Anagrafica. Un dossier deve
      aprirsi sulla sintesi: e' la scheda che risponde da sola, anche a chi non arrivera' mai
      alla seconda.
    */
    const summary = workbook.addWorksheet(sheetNames.summary);
    summary.columns = [{ width: 38 }, { width: 22 }, { width: 64 }];

    const title = summary.addRow([
        t('dashboard.overview.clients.export.summaryTitle', { name }),
    ]);
    title.font = { bold: true, size: 13 };

    // Il periodo sta in testa e non in fondo: e' la chiave di lettura di tutti i numeri
    // sotto, e un file che gira per email viene letto senza il pannello accanto.
    const period = summary.addRow([
        t('dashboard.overview.clients.export.periodLabel'),
        periodLabel(data.meta, t),
    ]);
    period.font = { bold: true };
    summary.getRow(period.number).getCell(2).alignment = { horizontal: 'left' };

    summary.addRow([
        t('dashboard.overview.clients.export.generatedOnLabel'),
        parseTimestamp(data.meta.generated_on)?.toLocaleString(locale) ?? '',
    ]);

    /*
      L'indice degli altri fogli, subito sotto il periodo.

      Serve a chi legge in un viewer che non mostra le linguette — Anteprima, la posta, un
      allegato aperto al volo sul telefono: senza, il file sembra finire qui. Elenca dove
      stanno viaggi, rate e importi, cosi' il lettore sa che deve aprirlo davvero.
    */
    const indexRow = summary.addRow([
        t('dashboard.overview.clients.export.indexLabel'),
        [
            `${sheetNames.profile}: ${t('dashboard.overview.clients.export.indexProfile')}`,
            `${sheetNames.itineraries}: ${t('dashboard.overview.clients.export.indexItineraries')}`,
            `${sheetNames.payments}: ${t('dashboard.overview.clients.export.indexPayments')}`,
            `${sheetNames.destinations}: ${t('dashboard.overview.clients.export.indexDestinations')}`,
            `${sheetNames.fees}: ${t('dashboard.overview.clients.export.indexFees')}`,
        ].join(' · '),
    ]);
    indexRow.font = { bold: true };
    // Unita a C e mandata a capo: da sola in B la riga verrebbe tagliata dalla colonna
    // delle note, che e' larga e piena.
    summary.mergeCells(indexRow.number, 2, indexRow.number, 3);
    indexRow.getCell(2).alignment = { wrapText: true, vertical: 'top' };
    indexRow.getCell(2).font = { color: { argb: 'FF374151' }, size: 10 };
    indexRow.height = 46;

    /*
      Un file senza righe deve dire perche' e' senza righe. Il sospetto principale e'
      l'interruttore del periodo: acceso, la scheda mostra solo la finestra scelta, e su un
      cliente fermo da mesi quella finestra e' legittimamente vuota. Senza questa riga
      l'export sembrerebbe rotto proprio quando sta funzionando.
    */
    if (report.emptyScope) {
        const warning = summary.addRow([
            t(
                data.meta.period_only
                    ? 'dashboard.overview.clients.export.emptyPeriodOnly'
                    : 'dashboard.overview.clients.export.emptyFullHistory'
            ),
        ]);
        warning.font = { bold: true, color: { argb: 'FFB45309' } };
    }

    summary.addRow([]);

    const kpiHeader = summary.addRow([
        t('dashboard.overview.clients.export.metricColumn'),
        t('dashboard.overview.clients.export.valueColumn'),
        t('dashboard.overview.clients.export.noteColumn'),
    ]);
    kpiHeader.font = { bold: true };
    kpiHeader.eachCell((cell: Ws) => {
        cell.fill = HEADER_FILL;
    });

    /*
      I tre importi restano tre righe distinte e non diventano mai un "valore generato":
      pagato e in attesa sono due stati dello stesso credito, il totale preventivato e' la
      loro somma gia' fatta dal database. Impilarli in un unico numero cancellerebbe
      l'unica cosa che il foglio deve dire, cioe' quanto di quel totale e' davvero entrato.
    */
    const kpiRows: Array<[string, string | number, string]> = [
        /*
          Una sola riga di conteggio viaggi, non due. `kpiTrips` si chiama gia' "Viaggi
          confermati" e conta gli itinerari con almeno una rata incassata: affiancargli
          `completed_count` produceva due righe con la stessa etichetta e due numeri che
          nella pratica coincidono (e' la ragione per cui la UI ha smesso di mostrarli come
          frazione "3 / 3"). Chi ha bisogno dello stato per stato lo legge nel foglio
          Itinerari, dove ogni riga porta il proprio.
        */
        [
            t('dashboard.overview.clients.kpiTrips'),
            kpis.itineraries_count,
            t('dashboard.overview.clients.export.noteTrips'),
        ],
        [t('dashboard.overview.clients.export.metricRequests'), kpis.requests_count, ''],
        [labelPaid, kpis.paid_amount, t('dashboard.overview.clients.export.notePaid')],
        [
            labelPending,
            kpis.remaining_amount,
            t('dashboard.overview.clients.export.notePending'),
        ],
        [labelQuoted, kpis.total_amount, t('dashboard.overview.clients.export.noteQuoted')],
        [
            t('dashboard.overview.clients.export.metricOverdue'),
            kpis.overdue_amount,
            t('dashboard.overview.clients.export.noteOverdue'),
        ],
        [
            t('dashboard.overview.clients.export.metricClosedValue'),
            kpis.closed_value_total,
            t('dashboard.overview.clients.export.noteClosedValue'),
        ],
    ];

    kpiRows.forEach(([label, value, note], index) => {
        const row = summary.addRow([label, value, note]);
        // Le prime due righe sono conteggi, il resto denaro.
        if (index >= 2) row.getCell(2).numFmt = MONEY_FORMAT;
        row.getCell(3).font = { color: { argb: 'FF6B7280' }, size: 9 };
        row.getCell(3).alignment = { wrapText: true, vertical: 'top' };
    });

    summary.views = [{ state: 'frozen', ySplit: kpiHeader.number }];

    // -------------------------------------------------------------------- Anagrafica
    const profile = workbook.addWorksheet(sheetNames.profile);
    addTable(
        profile,
        [
            { header: t('dashboard.overview.clients.export.fieldColumn'), width: 30 },
            { header: t('dashboard.overview.clients.export.valueColumn'), width: 46 },
        ],
        [
            [t('dashboard.overview.clients.export.fieldName'), name],
            [t('dashboard.overview.clients.export.fieldEmail'), client.email ?? ''],
            [t('dashboard.overview.clients.export.fieldPhone'), client.phone ?? ''],
            [
                t('dashboard.overview.clients.export.fieldHasAccount'),
                yesNo(client.has_account, t),
            ],
            [
                t('dashboard.overview.clients.export.fieldFirstSeen'),
                parseTimestamp(client.first_seen_at) ?? '',
            ],
            [
                t('dashboard.overview.clients.export.fieldLastSeen'),
                parseTimestamp(client.last_seen_at) ?? '',
            ],
            [t('dashboard.overview.clients.export.fieldClientKey'), client.client_key],
        ]
    );
    // Le due date dell'anagrafica stanno in colonna B insieme a del testo: il formato si
    // mette sulla cella, non sulla colonna, altrimenti Excel proverebbe a formattare anche
    // l'email.
    [6, 7].forEach((rowNumber) => {
        const cell = profile.getRow(rowNumber).getCell(2);
        if (cell.value instanceof Date) cell.numFmt = DATE_FORMAT;
    });

    // -------------------------------------------------------------------- Itinerari
    const trips = workbook.addWorksheet(sheetNames.itineraries);
    addTable(
        trips,
        [
            { header: t('dashboard.overview.detail.columnItinerary'), width: 38 },
            { header: t('dashboard.overview.detail.columnStatus'), width: 16 },
            { header: t('dashboard.overview.clients.export.columnDestinations'), width: 28 },
            { header: t('dashboard.overview.clients.export.columnStart'), width: 13, numFmt: DATE_FORMAT },
            { header: t('dashboard.overview.clients.export.columnEnd'), width: 13, numFmt: DATE_FORMAT },
            { header: t('dashboard.overview.clients.columnAdvisor'), width: 24 },
            { header: t('dashboard.overview.clients.export.columnHasPlan'), width: 14 },
            { header: t('dashboard.overview.clients.export.columnInstallments'), width: 10 },
            { header: labelPaid, width: 15, numFmt: MONEY_FORMAT },
            { header: labelPending, width: 15, numFmt: MONEY_FORMAT },
            { header: labelQuoted, width: 18, numFmt: MONEY_FORMAT },
            { header: t('dashboard.overview.clients.export.metricOverdue'), width: 15, numFmt: MONEY_FORMAT },
            { header: t('dashboard.overview.clients.export.columnCurrency'), width: 10 },
            { header: t('dashboard.overview.clients.export.columnCreatedAt'), width: 13, numFmt: DATE_FORMAT },
            { header: t('dashboard.overview.clients.export.columnCompletedAt'), width: 13, numFmt: DATE_FORMAT },
        ],
        data.itineraries.map((itinerary: DetailItinerary) => [
            itinerary.title,
            statusLabel(itinerary.status, t),
            itinerary.destinations.join(', '),
            parseDateOnly(itinerary.start_date) ?? '',
            parseDateOnly(itinerary.end_date) ?? '',
            itinerary.advisor_name ?? '',
            yesNo(itinerary.has_plan, t),
            itinerary.installments_count,
            // Senza piano rate i tre importi non esistono: la cella resta vuota, mai zero.
            // Uno zero in colonna "Pagato" si legge come "non ha pagato niente", che e'
            // un'affermazione diversa da "non c'e' ancora un piano".
            itinerary.has_plan ? itinerary.paid_amount : '',
            itinerary.has_plan ? itinerary.remaining_amount : '',
            itinerary.has_plan ? itinerary.plan_total : '',
            itinerary.has_plan ? itinerary.overdue_amount : '',
            itinerary.currency,
            parseTimestamp(itinerary.created_at) ?? '',
            parseTimestamp(itinerary.completed_at) ?? '',
        ])
    );

    // -------------------------------------------------------------------- Pagamenti
    const payments = workbook.addWorksheet(sheetNames.payments);
    addTable(
        payments,
        [
            { header: t('dashboard.overview.detail.columnItinerary'), width: 38 },
            { header: t('dashboard.overview.clients.export.columnSeq'), width: 8 },
            { header: t('dashboard.overview.clients.export.columnLabel'), width: 26 },
            { header: t('dashboard.overview.clients.export.columnDueDate'), width: 13, numFmt: DATE_FORMAT },
            { header: t('dashboard.overview.clients.export.columnAmount'), width: 15, numFmt: MONEY_FORMAT },
            { header: t('dashboard.overview.detail.columnStatus'), width: 18 },
            { header: t('dashboard.overview.clients.export.columnPaidAt'), width: 17, numFmt: DATETIME_FORMAT },
            { header: t('dashboard.overview.clients.export.columnPaidAmount'), width: 15, numFmt: MONEY_FORMAT },
            { header: t('dashboard.overview.clients.export.columnMethod'), width: 20 },
        ],
        // Appiattimento, non aggregazione: la RPC le manda annidate per la tabella della
        // UI, il foglio le vuole una per riga. Nessun valore cambia.
        data.itineraries.flatMap((itinerary: DetailItinerary) =>
            itinerary.installments.map((installment: DetailInstallment) => [
                itinerary.title,
                installment.seq,
                installment.label ?? '',
                parseDateOnly(installment.due_date) ?? '',
                installment.amount,
                installmentStatusLabel(installment, t),
                parseTimestamp(installment.paid_at) ?? '',
                installment.paid_amount ?? '',
                methodLabel(installment.payment_method, t),
            ])
        )
    );

    // -------------------------------------------------------------------- Destinazioni
    const destinations = workbook.addWorksheet(sheetNames.destinations);
    addTable(
        destinations,
        [
            { header: t('dashboard.overview.clients.export.columnDestination'), width: 36 },
            { header: t('dashboard.overview.clients.export.columnTripCount'), width: 12 },
        ],
        data.stats.destinations.map((destination) => [destination.destination, destination.count])
    );

    // ------------------------------------------------- Commissioni Place2Be da versare
    /*
      Foglio a parte, e non una colonna in fondo agli itinerari, perche' questo numero non
      appartiene al conto del cliente: lui ha gia' pagato. E' la commissione Place2Be
      maturata su incassi registrati a mano — quelli passati da Stripe la trattengono come
      application_fee e qui valgono zero — e resta a debito di chi ha incassato.
      L'etichetta lo dice per esteso in ogni riga di intestazione: chiamarlo "commissioni"
      e basta lo farebbe leggere come il margine dell'agenzia, che e' un altro numero e in
      questo payload non c'e' proprio.
    */
    const fees = workbook.addWorksheet(sheetNames.fees);
    fees.columns = [{ width: 38 }, { width: 8 }, { width: 15 }, { width: 17 }, { width: 20 }, { width: 22 }];

    const feesTitle = fees.addRow([t('dashboard.overview.clients.export.feesTitle')]);
    feesTitle.font = { bold: true, size: 13 };
    addNote(fees, t('dashboard.overview.clients.export.feesDisclaimer'));
    fees.addRow([]);

    const feesTotal = fees.addRow([
        t('dashboard.overview.clients.export.feesTotalLabel'),
        null,
        kpis.platform_fee_due_amount,
    ]);
    feesTotal.font = { bold: true };
    feesTotal.getCell(3).numFmt = MONEY_FORMAT;
    fees.addRow([]);

    const feesHeader = fees.addRow([
        t('dashboard.overview.detail.columnItinerary'),
        t('dashboard.overview.clients.export.columnSeq'),
        t('dashboard.overview.clients.export.columnDueDate'),
        t('dashboard.overview.clients.export.columnPaidAt'),
        t('dashboard.overview.clients.export.columnMethod'),
        t('dashboard.overview.clients.export.columnFeeDue'),
    ]);
    feesHeader.font = { bold: true };
    feesHeader.eachCell((cell: Ws) => {
        cell.fill = HEADER_FILL;
    });

    data.itineraries.forEach((itinerary: DetailItinerary) => {
        itinerary.installments
            // Solo le rate che una commissione la devono davvero: un foglio di zeri
            // nasconderebbe le tre righe che chiedono un versamento.
            .filter((installment: DetailInstallment) => installment.platform_fee_due > 0)
            .forEach((installment: DetailInstallment) => {
                const row = fees.addRow([
                    itinerary.title,
                    installment.seq,
                    parseDateOnly(installment.due_date) ?? '',
                    parseTimestamp(installment.paid_at) ?? '',
                    methodLabel(installment.payment_method, t),
                    installment.platform_fee_due,
                ]);
                row.getCell(3).numFmt = DATE_FORMAT;
                row.getCell(4).numFmt = DATETIME_FORMAT;
                row.getCell(6).numFmt = MONEY_FORMAT;
            });
    });

    if (fees.lastRow?.number === feesHeader.number) {
        addNote(fees, t('dashboard.overview.clients.export.feesEmpty'));
    }

    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
}

/** Nome file: `dossier-cliente_mario-rossi_2026-09-03.xlsx`. */
export function clientDossierFilename(data: ClientDetailResult, prefix: string): string {
    const slug =
        clientDisplayName(data.client)
            .toLowerCase()
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '') || 'cliente';
    const day = (data.meta.generated_on ?? '').slice(0, 10) || new Date().toISOString().slice(0, 10);
    return `${prefix}_${slug}_${day}.xlsx`;
}
